import { InteractionsPartRequestFormInitialPartType } from '../../../../../@types/interactions.type';
import { PartActionType } from '../../../../../@types/partAction.type';
import {
  PartTypeActionLiveSearchItem,
  PartTypeActionLiveSearchItemCategoryCode,
  PartTypeLiveSearchItem,
} from '../../../../../@types/partType.type';
import { PartTypeCategory } from '../../../../../configs/enums';

/**
 * Actions where the IN-side dropdown is pre-populated with the part-types
 * relevant to the job before the user types — installed-on-job part types
 * plus the full client/contract part-type universe. Mirrors cms1's
 * contract-allocated `part_type_allocations` flow.
 */
export const PRE_POPULATE_ACTIONS = new Set<PartActionType>([
  PartActionType.TEST,
  PartActionType.REPAIR,
  PartActionType.ADJUST,
  PartActionType.CLEAN,
  PartActionType.DOA,
]);

/**
 * Map a `PartTypeCategory` to the autocomplete's narrower
 * `PartTypeActionLiveSearchItemCategoryCode`. Consumable maps 1:1; both
 * Serialised and Kit collapse to Serialised — kits behave like serialised
 * parts in the action UI (they have a parent serial and follow the same
 * "needs a specific serial" rules), and `PartTypeActionLiveSearchItem`
 * intentionally only models the Serialised/Consumable dichotomy that the
 * live-search BFF returns.
 */
const toCategoryCode = (raw: string | undefined): PartTypeActionLiveSearchItemCategoryCode => {
  if (raw === PartTypeCategory.Consumable)
    return PartTypeActionLiveSearchItemCategoryCode.Consumable;
  // Serialised + Kit + anything else (e.g. unset) → treat as serialised.
  return PartTypeActionLiveSearchItemCategoryCode.Serialised;
};

/**
 * Shape an `/partTypes/service/{id}/condition/installed` row into the
 * autocomplete's expected `PartTypeActionLiveSearchItem` shape. Installed
 * rows have a condition code but no serial — the user selects the part type,
 * the form gathers the serial separately.
 */
export const normalizeInstalledPartType = (
  item: InteractionsPartRequestFormInitialPartType
): PartTypeActionLiveSearchItem => ({
  name: item.name,
  partTypeId: item.id,
  partTypeName: item.name,
  partTypeNumber: item.number,
  categoryCode: toCategoryCode(item.categoryCode),
  conditionCode: item.conditionCode || '',
  serial1: '',
  softwareVersion: '',
  hardwareVersion: '',
  firmwareVersion: '',
  fleetTagName: '',
  locationName: '',
  locationId: 0,
  serviceId: 0,
  searchItemType: 'part-type',
});

/**
 * Shape an `/partTypes/client/{clientId}?contractId={id}` row — the
 * client/contract part-type universe — into the autocomplete's shape.
 */
export const normalizeContractPartType = (
  item: PartTypeLiveSearchItem
): PartTypeActionLiveSearchItem => ({
  name: item.name,
  partTypeId: item.id,
  partTypeName: item.name,
  partTypeNumber: item.number,
  categoryCode: toCategoryCode(item.categoryCode),
  conditionCode: '',
  serial1: '',
  softwareVersion: '',
  hardwareVersion: '',
  firmwareVersion: '',
  fleetTagName: '',
  locationName: '',
  locationId: 0,
  serviceId: 0,
  searchItemType: 'part-type',
});

/**
 * Combine reported-serial specifics + installed parts + client/contract universe
 * into a single default list. Order, top to bottom:
 *   1. The reported-serial part itself (so it's the first item — the most
 *      common selection for Test/Repair on a serialised job).
 *   2. Anything else from the reported-serial /partOrPartType response
 *      (related part-types and other related serials).
 *   3. Part types currently installed on this job.
 *   4. The full client/contract part-type universe.
 * De-duped by `partTypeId` for part-types and by `partId` (when present) for
 * specific parts.
 */
export const buildDefaultPartTypeOptions = (
  reportedSerialResults: PartTypeActionLiveSearchItem[] | undefined,
  reportedSerialNumberPartId: number | undefined,
  installed: InteractionsPartRequestFormInitialPartType[] | undefined,
  universe: PartTypeLiveSearchItem[] | undefined
): PartTypeActionLiveSearchItem[] => {
  // Pull out the reported part itself, then anything else the /partOrPartType
  // response surfaced (related types / serials).
  const reported = reportedSerialResults ?? [];
  const reportedHero = reportedSerialNumberPartId
    ? reported.find((r) => r.partId === reportedSerialNumberPartId)
    : undefined;
  const reportedRest = reported.filter((r) => r !== reportedHero);

  // The reported set already populates rows for these part-types; don't re-emit
  // them via installed/universe sources.
  const reportedPartTypeIds = new Set(reported.map((r) => r.partTypeId));

  const installedRows = (installed ?? [])
    .map(normalizeInstalledPartType)
    .filter((r) => !reportedPartTypeIds.has(r.partTypeId));
  const installedIds = new Set(installedRows.map((r) => r.partTypeId));

  const universeRows = (universe ?? [])
    .filter((r) => !installedIds.has(r.id) && !reportedPartTypeIds.has(r.id))
    .map(normalizeContractPartType);

  const head = reportedHero ? [reportedHero] : [];
  return [...head, ...reportedRest, ...installedRows, ...universeRows];
};
